import {useCallback, useEffect, useState} from "react";
import {useDispatch} from "react-redux";

import {getUser, upsertSettings} from "../../slices/UserSlice";
import {SessionStorageManager} from "../../utilities/generalConstants.js/generalConstants";
import {swalCustom} from "../../utilities/hooks/swalCustom";
import {toaster} from "../../utilities/hooks/toaster";
import {KP_FIN_TABLE_STORAGE_KEY} from "../KP/kp-f/kpFinTable/utils/kpFinColumns";

const getSettingsFromUser = user => {
  const hiddenColumns = Array.isArray(user?.settings?.hiddenColumns) ? user.settings.hiddenColumns : [];
  const columnsPosition = Array.isArray(user?.settings?.columnsPosition) ? user.settings.columnsPosition : [];

  return {hiddenColumns, columnsPosition};
};

const useKpFinHiddenColumns = () => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [initialSettings, setInitialSettings] = useState(undefined);
  const [tableSettings, setTableSettings] = useState({hiddenColumns: [], columnsPosition: []});

  useEffect(() => {
    dispatch(getUser())
      .unwrap()
      .then(user => {
        setInitialSettings(getSettingsFromUser(user));
      })
      .catch(error => {
        console.error(error);
      });
  }, [dispatch]);

  const onTableSettingsChange = useCallback(settings => {
    setTableSettings(settings || {hiddenColumns: [], columnsPosition: []});
  }, []);

  const onSaveClick = useCallback(() => {
    if (isSaving) return;
    setIsSaving(true);

    dispatch(upsertSettings({type: "hiddenColumns", data: tableSettings?.hiddenColumns || []}))
      .unwrap()
      .then(() =>
        dispatch(upsertSettings({type: "columnsPosition", data: tableSettings?.columnsPosition || []})).unwrap(),
      )
      .then(() => {
        toaster.success("Co(f) columns settings successfully saved!");
        setIsSaving(false);
      })
      .catch(error => {
        console.error(error);
        setIsSaving(false);
      });
  }, [dispatch, isSaving, tableSettings]);

  const onResetConfirm = useCallback(() => {
    if (isSaving) return;

    const kpFinTableStorageManager = new SessionStorageManager(KP_FIN_TABLE_STORAGE_KEY);
    kpFinTableStorageManager.setItem([]);

    const resetSettings = {hiddenColumns: [], columnsPosition: []};
    setTableSettings(resetSettings);
    setInitialSettings(resetSettings);
    setIsSaving(true);

    dispatch(upsertSettings({type: "hiddenColumns", data: []}))
      .unwrap()
      .then(() => dispatch(upsertSettings({type: "columnsPosition", data: []})).unwrap())
      .then(() =>
        dispatch(getUser())
          .unwrap()
          .then(user => {
            const freshSettings = getSettingsFromUser(user);
            setInitialSettings(freshSettings);
            setTableSettings(freshSettings);
          }),
      )
      .then(() => {
        toaster.success("Co(f) columns settings successfully cleared!");
        setIsSaving(false);
      })
      .catch(error => {
        console.error(error);
        setIsSaving(false);
      });
  }, [dispatch, isSaving]);

  const onResetClick = useCallback(() => {
    if (isSaving) return;

    swalCustom.confirm({
      text: "Are you sure you want to clear Co(f) columns settings?",
      confirmFunc: onResetConfirm,
    });
  }, [isSaving, onResetConfirm]);

  return {
    initialSettings,
    isSaving,
    onResetClick,
    onSaveClick,
    onTableSettingsChange,
  };
};

export default useKpFinHiddenColumns;
